/*
* Add Order Bid view for authenticated users
*/

import { filterBy } from '@progress/kendo-data-query';
import * as _ from 'lodash';
import * as React from 'react';
import { Button, ButtonToolbar, Row, Well, Modal } from 'react-bootstrap';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { push } from 'react-router-redux';
import { compose } from 'redux';
import { connectRequest } from 'redux-query';

import * as Actions from '../actions';
import * as MutateActions from '../actions/mutate';
import * as AppComp from '../common/app-component-base';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../common/formatters';
import * as KendoWrappers from '../common/kendoforms/componentwrappers';
import { NavigationLinks } from '../common/navigation';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';
import ApiEndpoints from '../config/apiendpoints';
import * as LK from '../config/apilookupkeys';
import * as DetailFormCommon from '../inventory/detail-form/common';
import * as ReduxQueries from '../queries';
import Auth from '../auth/auth';
import { AuthConfig } from '../config/authconfiguration';

export interface IOrderAddBidProps extends AppComp.IAppComponentProps {
    lookups?: any;
    newOrder?: any;
    navigateTo?: any;
    addBid?: any;
    addBidRedirect?: any;
    dispatch?: any;
    push?: any;
}

export interface IOrderAddBidState extends AppComp.IAppComponentState {
    filterAssociation?: any;
    filterClient?: any;
    filterClientContact?: any;
    filterClientRegion?: any;
    filterManagerUser?: any;
    filterSiteInspectorUser?: any;
    formChanges?: any;
    skipAssociation?: any;
    skipClient?: any;
    skipClientContact?: any;
    skipClientRegion?: any;
    skipManagerUser?: any;
    skipSiteInspectorUser?: any;
    showModalConfirmAddBid?: boolean;
}

class AddOrderBid extends AppComp.AppComponent<IOrderAddBidProps, IOrderAddBidState> {
    public static readonly AddOrderBidInputNames = {
        clientName: 'client-name',
        clientContact: 'client-contact',
        associationName: 'association-name',
        projectManager: 'project-manager',
        siteInspector: 'site-inspector',
        resetAssocClientContact: 'reset-assoc-client-contact',
    };

    public readonly virtualizedDropdownPageSize: number = 20;
    public originalFormDataCache = {};
    public kendoFormWhiteBoxSectionProps = {};

    public constructor(props: IOrderAddBidProps) {
        super(props, 'app.order-add-bid.');

        this.state = {
            filterAssociation: undefined,
            filterClient: undefined,
            filterClientContact: undefined,
            filterClientRegion: undefined,
            filterManagerUser: undefined,
            filterSiteInspectorUser: undefined,
            formChanges: {},
            skipAssociation: 0,
            skipClient: 0,
            showModalConfirmAddBid: false,
            skipClientContact: 0,
            skipClientRegion: 0,
            skipManagerUser: 0,
            skipSiteInspectorUser: 0,
        };
    }

    public componentDidUpdate() {
        let resetFormData = this.getFormChangesResetRequired();

        if (resetFormData && _.size(this.state.formChanges)) {
            this.setState({ formChanges: {} });
        }
    }

    // Handlers
    public handleConfirmAddBidButton(e: any) {
        this.setState({ showModalConfirmAddBid: true });
    }

    public handleConfirmAddBidButtonAction(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = _.merge(
            {},
            {
                association: {
                    associationId: _.get(this.state, 'formChanges.newOrderAssociationId', 0),
                    // resetAssociationClientContact: Formatters.getBool(this.state, 'formChanges.newOrderResetAssociationClientContact'),
                }
            },
            { client: { clientId: _.get(this.state, 'formChanges.newOrderClientId') } },
            { contact: { contactId: _.get(this.state, 'formChanges.newOrderClientContactId') } },
            { managerUser: { userObjId: _.get(this.state, 'formChanges.newOrderManagerUserId') } },
            { siteInspectorUser: { userObjId: _.get(this.state, 'formChanges.newOrderSiteInspectorUserId') } },
        );

        this.props.newOrder(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};
                
                // FORCE RELOAD
                const newOrderId = _.get(response, 'body.orderId');

                let auth = new Auth();
                auth.setLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBid, false);
                auth.clearLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBidNewClientId);
                auth.clearLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBidNewClientContactId);
                auth.clearLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBidNewAssociationId);

                this.props.navigateTo(NavigationLinks.orderEdit(newOrderId));
            } else {
              // Error during creation...
            }
        });
    }

    public handleCancelAndCloseModalConfirmAddBid() {
        this.setState({ showModalConfirmAddBid: false });
    }

    public handleAddAssociationButton(e: any) {
        let auth = new Auth();
        auth.setLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBid, true);
        auth.clearLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBidNewAssociationId);
        this.props.navigateTo(NavigationLinks.associationAdd());
    }

    public handleAddNewBidCancelButton(e: any) {
        let auth = new Auth();
        auth.setLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBid, false);
        auth.setLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBidNewClientId, 0);
        auth.setLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBidNewAssociationId, 0);
        auth.setLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBidNewClientContactId, 0);
        // auth.clearLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBid);
        // auth.clearLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBidNewClientId);
        // auth.clearLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBidNewAssociationId);
        // auth.clearLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBidNewClientContactId);
        this.props.navigateTo(NavigationLinks.orderAddBid());
    }

    public handleAddClientButton(e: any) {
        // Ensure that after redirect and new client added, user redirected back to this page with newly added client selected

        let auth = new Auth();
        auth.setLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBid, true);
        auth.clearLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBidNewClientId);
        auth.clearLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBidNewClientContactId);
        this.props.navigateTo(NavigationLinks.clientAdd());
    }

    public handleAddClientContactButton(e: any) {
        const clientId = Formatters.getInt(this.state, 'formChanges.newOrderClientId');
        let auth = new Auth();
        auth.setLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBid, true);
        auth.clearLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBidNewClientId);
        auth.clearLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBidNewClientContactId);
        this.props.navigateTo(NavigationLinks.clientEdit(clientId));
    }

    public handleCreateBidButton(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = _.merge(
            {},
            {
                association: {
                    associationId: _.get(this.state, 'formChanges.newOrderAssociationId', 0),
                    // resetAssociationClientContact: Formatters.getBool(this.state, 'formChanges.newOrderResetAssociationClientContact'),
                }
            },
            { client: { clientId: _.get(this.state, 'formChanges.newOrderClientId') } },
            { contact: { contactId: _.get(this.state, 'formChanges.newOrderClientContactId') } },
            { managerUser: { userObjId: _.get(this.state, 'formChanges.newOrderManagerUserId') } },
            { siteInspectorUser: { userObjId: _.get(this.state, 'formChanges.newOrderSiteInspectorUserId') } },
        );

        this.props.newOrder(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};
                
                // FORCE RELOAD
                const newOrderId = _.get(response, 'body.orderId');

                this.props.navigateTo(NavigationLinks.orderEdit(newOrderId));
            } else {
              // Error during creation...
            }
        });        
    }

    public handleFormInputChange(e: any, x: any) {
        // console.log('handleForminputchange', {e: e, x: x });

        let getChangesResult = this.getFormChanges(e, x);
        if (getChangesResult.applyChanges) {            
            this.setState({
                formChanges: getChangesResult.formChanges
            });
        }
    }

    // Client
    public handleFormInputClientChange(e: any, x: any) {
        const { lookups, ...rest } = this.props;
        const { clients, ...restClients } = lookups;

        let getChangesResult = this.getFormChanges(e, x);

        if (getChangesResult.applyChanges) {
            let fc = _.cloneDeep(_.get(getChangesResult, 'formChanges', {}));

            const newOrderClientId = Formatters.getInt(fc, 'newOrderClientId');
            const newOrderClient = _.find(clients, [ 'clientId', newOrderClientId ]);
            const newOrderClientRegionId = Formatters.getInt(newOrderClient, 'region.regionId');
            const def = this.getDefRegionUsers(lookups, newOrderClientRegionId);

            const manUserId = Formatters.getStr(def, 'defaultManager._id');
            const siUserId = Formatters.getStr(def, 'defaultSiteInspector._id');

            _.set(fc, 'newOrderManagerUserId', manUserId);
            _.set(fc, 'newOrderSiteInspectorUserId', siUserId);

            _.set(fc, 'newOrderClientContactId', 0);
            _.set(fc, 'newOrderClientContactOptions', []);

            // Do not clear newOrderAssociationId, it may have been set already
            // _.set(fc, 'newOrderAssociationOptions', []);
            // _.set(fc, 'newOrderAssociationId', 0);

            this.setState({
                formChanges: _.cloneDeep(fc),
                filterClientContact: undefined,
                filterAssociation: undefined,
            });
        }
    }

    public handleClientDropdownPageChange(event: any, formData: any) {
        this.setState({
            skipClient: event.page.skip
        });
    }

    public handleClientDropdownFilterChange(event: any, formData: any) {
        this.setState({
            skipClient: 0,
            filterClient: event.filter,
            filterClientContact: undefined,
            filterAssociation: undefined,
        });
    }

    // Client Contact
    public handleFormInputClientContactChange(e: any, x: any) {
        let getChangesResult = this.getFormChanges(e, x);

        // console.log(`handleFormInputClientContactChange getChangesResult.applyChanges ${getChangesResult.applyChanges}`, { e, x, getChangesResult });

        if (getChangesResult.applyChanges) {
            let fc = _.cloneDeep(_.get(getChangesResult, 'formChanges', {}));
            // _.set(fc, 'newOrderAssociationOptions', []);
            // _.set(fc, 'newOrderClientContactId', 0);
            // _.set(fc, 'newOrderClientContactOptions', []);

            // console.log('handleFormInputClientContactChange reset contact, etc fc', { fc });
            this.setState({
                formChanges: _.cloneDeep(fc),
                // filterClientContact: undefined,
                filterAssociation: undefined,
            });
        }
    }

    public handleClientContactDropdownPageChange(event: any, formData: any) {
        this.setState({
            skipClientContact: event.page.skip
        });
    }

    public handleClientContactDropdownFilterChange(event: any, formData: any) {
        this.setState({
            skipClientContact: 0,
            filterClientContact: event.filter,
        });
    }

    // Assoc
    public handleFormInputAssociationChange(e: any, x: any) {
        const { associations, clients, ...rest } = this.props.lookups;

        const newAssocId = Formatters.getInt(e, 'target.value');

        const curNewOrderClientId = Formatters.getInt(this.state, 'formChanges.newOrderClientId');
        const curNewOrderClientContactId = Formatters.getInt(this.state, 'formChanges.newOrderClientContactId');

        let newClientId = 0;
        let newClientContactId = 0;
        let setNewClientIds = false;
        if (curNewOrderClientId === 0 && curNewOrderClientContactId === 0) {
            if (_.size(associations) && (newAssocId > 0)) {
                const newOrderSelectedAssocOrig = _.find(associations, [ 'associationId', newAssocId ]);
                const newOrderSelectedAssocOrigClientId = _.get(newOrderSelectedAssocOrig, 'client.clientId');
                const newOrderSelectedAssocOrigClient = _.find(clients, [ 'clientId', newOrderSelectedAssocOrigClientId ]);

                newClientId = Formatters.getInt(newOrderSelectedAssocOrigClient, 'clientId');
                newClientContactId = Formatters.getInt(newOrderSelectedAssocOrig, 'contact.contactId');
                setNewClientIds = true;
            }
        }

        let getChangesResult = this.getFormChanges(e, x);
        if (getChangesResult.applyChanges) {
            let fc = _.cloneDeep(_.get(getChangesResult, 'formChanges', {}));

            if (setNewClientIds) {
                _.set(fc, 'newOrderClientId', newClientId);
                _.set(fc, 'newOrderClientContactId', newClientContactId);
            }

            this.setState({
                formChanges: _.cloneDeep(fc),
            });
        }
    }

    public handleAssociationDropdownPageChange(event: any, formData: any) {
        this.setState({
            skipAssociation: event.page.skip
        });
    }

    public handleAssociationDropdownFilterChange(event: any, formData: any) {
        this.setState({
            skipAssociation: 0,
            filterAssociation: event.filter,
        });
    }

    // Manager User
    public handleManagerUserDropdownPageChange(event: any, formData: any) {
        this.setState({
            skipManagerUser: event.page.skip
        });
    }

    public handleManagerUserDropdownFilterChange(event: any, formData: any) {
        this.setState({
            skipManagerUser: 0,
            filterManagerUser: event.filter,
        });
    }

    // Site Inspector User
    public handleSiteInspectorUserDropdownPageChange(event: any, formData: any) {
        this.setState({
            skipSiteInspectorUser: event.page.skip
        });
    }

    public handleSiteInspectorUserDropdownFilterChange(event: any, formData: any) {
        this.setState({
            skipSiteInspectorUser: 0,
            filterSiteInspectorUser: event.filter,
        });
    }

    // Order button
    public handleCreateOrderButton(event: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = _.merge(
            {},
            { association: { associationId: _.get(event, 'associationId', 0) } },
            { client: { clientId: _.get(event, 'client.clientId') } },
        );

        this.props.newOrder(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};
                
                // FORCE RELOAD
                const newOrderId = _.get(response, 'body.orderId');

                this.props.navigateTo(NavigationLinks.orderEdit(newOrderId));
            } else {
              // Error during creation...
            }
        });
    }

    // Helpers
    public getDefRegionUsers(lookups: any, regionId: number = 0) {
        const { regions, users, ...rest } = lookups;

        // const newOrderClientRegionId = Formatters.getInt(this.state.formChanges, 'newOrderClientRegionId');

        let defaultManagerUser = _.stubObject();
        let defaultSiteInspectorUser = _.stubObject();
        if (_.size(regions) && _.size(users) && (regionId > 0)) {
            let orderClientRegion = _.find(regions, [ 'regionId', regionId ]);

            const regionDefaultManagerUser = _.get(orderClientRegion, 'defaultManager', {});
            const regionDefaultManagerUserObjId = Formatters.getStr(regionDefaultManagerUser, 'userObjectId');

            const regionDefaultSiteInspectorUser = _.get(orderClientRegion, 'defaultSiteInspector', {});
            const regionDefaultSiteInspectorUserObjId = Formatters.getStr(regionDefaultSiteInspectorUser, 'userObjectId');

            const orderUsers = _.map(users, (c) => {
                c.name = Formatters.composeUsernameLabel(c, true);
                return c;
            });

            defaultManagerUser = _.find(orderUsers, [ '_id', regionDefaultManagerUserObjId ]);
            defaultSiteInspectorUser = _.find(orderUsers, [ '_id', regionDefaultSiteInspectorUserObjId ]);
        }

        return {
            regionId: regionId,
            defaultManager: _.cloneDeep(defaultManagerUser),
            defaultSiteInspector: _.cloneDeep(defaultSiteInspectorUser),
        };
    }

    public getFormChanges(e: any, x: any) {
        let result = {
            applyChanges: false,
            formChanges: {},
            formChangesResult: {},
        };

        let fieldName = '';
        let newValue;

        let formChangesCopy = _.cloneDeep(this.state.formChanges);

        if (_.get(e, 'target.props.name')) {
            // Kendo form element
            if (_.has(e, 'target.combobox')) {
                // combobox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'target.value');
            } else {
                // assume textbox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'value');
            }
        } else {
            const eventTargetType = _.get(e, 'target.type', '');

            switch (eventTargetType) {
                case 'textarea':
                case 'text':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');
                    break;

                case 'kendo-dropdownlist':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value', '');
                    break;

                case 'virtualized-select':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(x, 'value');
                    break;

                case 'delete-button':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');
                    break;

                default:
                    console.log(`*** handleFormInputChange switch hit default for eventTargetType ${eventTargetType}`);
                    break;
            }
        }

        // console.log(`handleFormInputChange continuing 1 fieldName ${fieldName} newValue ${newValue}`, { e: e, x: x });

        if (!fieldName) { return result; }

        let applyChange = false;
        let origVal = _.get(this.originalFormDataCache, fieldName);
        let formChangesResult = {};
        if (_.isArray(origVal)) {
            if (_.isArray(newValue)) {
                if (!_.isEqual(origVal, newValue)) {
                    _.set(formChangesCopy, fieldName, _.cloneDeep(newValue));
                    _.set(formChangesResult, fieldName, _.cloneDeep(newValue));
                    applyChange = true;
                } else {
                    applyChange = false;
                }
            } else {
                if (_.includes(origVal, newValue)) {
                    // console.log(`original array already contained val ${newValue} origVal`, origVal);
                    applyChange = false;
                } else {
                    const formChangesArr = _.get(formChangesCopy, fieldName, []);
                    // console.log(`${fieldName} formChangesArr`, formChangesArr);
                    if (_.includes(formChangesArr, newValue)) {
                        // console.log(`formChangesArr field ${fieldName} array already contains val ${newValue} formChangesArr`, formChangesArr);
                        applyChange = false;
                    } else {
                        formChangesArr.push(newValue);
                        _.set(formChangesCopy, fieldName, _.cloneDeep(formChangesArr));
                        _.set(formChangesResult, fieldName, _.cloneDeep(formChangesArr));
                        applyChange = true;
                        // console.log(`formChangesArr field ${fieldName} array DID NOT have val ${newValue}, applying change`, formChangesArr);
                    }
                }
            }
        } else {
            if (String(newValue) !== String(origVal)) {
                // New value
                _.set(formChangesCopy, fieldName, newValue);
                _.set(formChangesResult, fieldName, newValue);
                applyChange = true;
            } else {
                // Same as orig val
                if (_.has(formChangesCopy, fieldName)) {
                    _.unset(formChangesCopy, fieldName);
                    _.set(formChangesResult, fieldName, newValue);
                    applyChange = true;
                }
            }
        }

        if (applyChange) {
            result.applyChanges = true;
            result.formChanges = _.cloneDeep(formChangesCopy);
            result.formChangesResult = _.cloneDeep(formChangesResult);
        }

        // console.log(`getFormChanges returns result`, result);

        return result;
    }

    public getFormData(lookups: any) {
        const { associations, clients, regions, users, ...rest } = lookups;

        let auth = new Auth();
        const lsRedirect: boolean = Formatters.bool(auth.getLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBid));
        let lsRedirNewClientId: number = (lsRedirect) ? Formatters.int(auth.getLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBidNewClientId)) : 0; // 20200214: 0;
        let lsRedirNewClientContactId: number = (lsRedirect) ? Formatters.int(auth.getLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBidNewClientContactId)) : 0; // 20200214 0;
        let lsRedirNewAssociationId: number = (lsRedirect) ? Formatters.int(auth.getLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBidNewAssociationId)) : 0; // 20200214 0;

        // if (lsRedirect) {
        //     const lsRedirNewClientIdLS = auth.getLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBidNewClientId);
        //     lsRedirNewClientId = Formatters.int(lsRedirNewClientIdLS);
        //     lsRedirNewClientContactId = Formatters.int(auth.getLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBidNewClientContactId));
        //     lsRedirNewAssociationId = Formatters.int(auth.getLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBidNewAssociationId));
        // }

        console.log(`add-bid getFormData1 lsRedirect ${lsRedirect} lsRedirNewClientId ${lsRedirNewClientId} lsRedirNewClientContactId ${lsRedirNewClientContactId} lsRedirNewAssociationId ${lsRedirNewAssociationId}`);

        let newOrderClientId = Formatters.getInt(this.state.formChanges, 'newOrderClientId', lsRedirNewClientId);
        let newOrderClientContactId = Formatters.getInt(this.state.formChanges, 'newOrderClientContactId', lsRedirNewClientContactId);
        let newOrderAssociationId = Formatters.getInt(this.state.formChanges, 'newOrderAssociationId', lsRedirNewAssociationId);
        const newOrderManagerUserId = Formatters.getStr(this.state.formChanges, 'newOrderManagerUserId');
        const newOrderSiteInspectorUserId = Formatters.getStr(this.state.formChanges, 'newOrderSiteInspectorUserId');

        console.log(`add-bid getFormData2 lsRedirect ${lsRedirect}` +
            ` lsRedirNewClientId ${lsRedirNewClientId} newOrderClientId ${newOrderClientId}` +
            ` lsRedirNewClientContactId ${lsRedirNewClientContactId} newOrderClientContactId ${newOrderClientContactId}` +
            ` lsRedirNewAssociationId ${lsRedirNewAssociationId} newOrderAssociationId ${newOrderAssociationId}`);

        let newOrderClientOptions: any = _.stubArray();
        if (_.size(clients)) {
            const regionClients = clients;

            newOrderClientOptions = _.orderBy(regionClients, ['name'], ['asc']).map((c) => _.merge({}, { value: Formatters.getInt(c, 'clientId'), text: Formatters.getStr(c, 'name') }));

            if (this.state.filterClient) {
                newOrderClientOptions = filterBy(newOrderClientOptions, this.state.filterClient);
            }
        }

        let newOrderClientContactOptions: any = _.stubArray();
        if (_.size(newOrderClientOptions) && _.size(clients) && (newOrderClientId > 0)) {
            let orderClient = _.find(clients, [ 'clientId', newOrderClientId ]);

            const orderClientContactsRaw = _.get(orderClient, 'contacts', []);
            const orderClientContacts = _.map(orderClientContactsRaw, (c) => {
                c.name = Formatters.composeClientContactFormal(c);
                return c;
            });

            let contactsFiltered = _.stubArray();
            contactsFiltered = orderClientContacts;

            newOrderClientContactOptions = _.orderBy(contactsFiltered, ['name'], ['asc']).map((c) => _.merge({}, {
                value: Formatters.getInt(c, 'contactId'),
                text: Formatters.getStr(c, 'name')
            }));

            if (this.state.filterClientContact) {
                newOrderClientContactOptions = filterBy(newOrderClientContactOptions, this.state.filterClientContact);
            }
        }

        let newOrderAssociationOptions: any = _.stubArray();
        if (_.size(associations)) {
            // Rob request: show all associations regardless of client
            const clientAssociations = associations;

            newOrderAssociationOptions = _.orderBy(clientAssociations, ['name'], ['asc']).map((c) => _.merge({}, { value: Formatters.getInt(c, 'associationId'), text: Formatters.getStr(c, 'name') }));

            if (this.state.filterAssociation) {
                newOrderAssociationOptions = filterBy(newOrderAssociationOptions, this.state.filterAssociation);
            }
        }

        let newOrderSelectedAssociation: any = _.stubObject();
        let newOrderSelectedAssociationClient: any = _.stubObject();
        if (_.size(associations) && (newOrderAssociationId > 0)) {
            const newOrderSelectedAssocOrig = _.find(associations, [ 'associationId', newOrderAssociationId ]);
            const newOrderSelectedAssocOrigClientId = _.get(newOrderSelectedAssocOrig, 'client.clientId');
            const newOrderSelectedAssocOrigClient = _.find(clients, [ 'clientId', newOrderSelectedAssocOrigClientId ]);

            newOrderSelectedAssociation = newOrderSelectedAssocOrig;
            newOrderSelectedAssociationClient = newOrderSelectedAssocOrigClient;
        }

        let newOrderSelectedClient = _.stubObject();
        if (_.size(clients) && (newOrderClientId > 0)) {
            const newOrderSelectedClientOrig = _.find(clients, [ 'clientId', newOrderClientId ]);

            newOrderSelectedClient = newOrderSelectedClientOrig;
        }

        let newOrderSelectedClientRegionId = 0;
        if (_.size(clients) && (newOrderClientId > 0)) {
            const newOrderSelectedClientOrig = _.find(clients, [ 'clientId', newOrderClientId ]);

            // newOrderSelectedClient = newOrderSelectedClientOrig;
            newOrderSelectedClientRegionId = Formatters.getInt(newOrderSelectedClientOrig, 'region.regionId');
        }

        let newOrderManagerUserOptions: any = _.stubArray();
        let newOrderSiteInspectorUserOptions: any = _.stubArray();
        if (_.size(regions) && _.size(users) && (newOrderSelectedClientRegionId > 0)) {
            const orderUsers = _.map(users, (c) => {
                c.name = Formatters.composeUsernameLabel(c, true);
                return c;
            });

            let managerUsersFiltered = _.cloneDeep(orderUsers);
            let siteInspectorUsersFiltered = _.cloneDeep(orderUsers);

            newOrderManagerUserOptions = _.orderBy(managerUsersFiltered, ['name'], ['asc']).map((c) => _.merge({}, {
                value: Formatters.getStr(c, '_id'),
                text: Formatters.getStr(c, 'name')
            }));

            newOrderSiteInspectorUserOptions = _.orderBy(siteInspectorUsersFiltered, ['name'], ['asc']).map((c) => _.merge({}, {
                value: Formatters.getStr(c, '_id'),
                text: Formatters.getStr(c, 'name')
            }));

            if (this.state.filterManagerUser) {
                newOrderManagerUserOptions = filterBy(newOrderManagerUserOptions, this.state.filterManagerUser);
            }

            if (this.state.filterSiteInspectorUser) {
                newOrderSiteInspectorUserOptions = filterBy(newOrderSiteInspectorUserOptions, this.state.filterSiteInspectorUser);
            }
        }

        let result = {
            newOrderAssociationId: newOrderAssociationId,
            newOrderClientId: newOrderClientId,
            newOrderClientContactId: newOrderClientContactId,
            newOrderClientRegionId: newOrderSelectedClientRegionId,
            newOrderSelectedClientRegionId: newOrderSelectedClientRegionId,
            newOrderAssociationOptions: newOrderAssociationOptions,
            newOrderSelectedClient: newOrderSelectedClient,
            newOrderSelectedAssociation: newOrderSelectedAssociation,
            newOrderSelectedAssociationClient: newOrderSelectedAssociationClient,
            newOrderClientOptions: newOrderClientOptions,
            newOrderClientContactOptions: newOrderClientContactOptions,
            newOrderManagerUserOptions: newOrderManagerUserOptions,
            newOrderManagerUserId: newOrderManagerUserId,
            newOrderSiteInspectorUserId: newOrderSiteInspectorUserId,
            newOrderSiteInspectorUserOptions: newOrderSiteInspectorUserOptions,
        };

        const res = _.merge(result, this.state.formChanges);
        console.log(`add-bid getFormData10`, { result, res });
        return res;
    }

    public getFormChangesCount() {
        return _.size(this.state.formChanges);
    }

    public getFormChangesValidateSave(formData: any) {
        let result = false;

        if (
            (Formatters.getInt(formData, 'newOrderClientRegionId') > 0)
            && (Formatters.getInt(formData, 'newOrderClientId') > 0)
            && (Formatters.getInt(formData, 'newOrderAssociationId') > 0)
            && (Formatters.getInt(formData, 'newOrderManagerUserId') > 0) // New 20200109
            && (Formatters.getInt(formData, 'newOrderSiteInspectorUserId') > 0) // New 20200109
        ) {
            result = true;
        }

        return result;
    }

    public getFormChangesResetRequired() {
        let resetFormData = false;
        
        return resetFormData;      
    }

    public getInputDisabled(inputName: string, formData: any) {
        let result = false;

        if (!inputName || !formData) { return result; }

        switch (inputName) {
            case AddOrderBid.AddOrderBidInputNames.clientName:
                // result = (Formatters.getInt(formData, 'newOrderClientRegionId') <= 0) ? true : false;
                result = false;
                break;

            case AddOrderBid.AddOrderBidInputNames.clientContact:
                result = (Formatters.getInt(formData, 'newOrderClientId') <= 0) ? true : false;
                break;

            case AddOrderBid.AddOrderBidInputNames.associationName:
                // result = (Formatters.getInt(formData, 'newOrderClientId') <= 0) ? true : false;
                result = false;
                break;

            case AddOrderBid.AddOrderBidInputNames.projectManager:
                result = (Formatters.getInt(formData, 'newOrderClientId') <= 0) ? true : false;
                break;

            case AddOrderBid.AddOrderBidInputNames.siteInspector:
                result = (Formatters.getInt(formData, 'newOrderClientId') <= 0) ? true : false;
                break;

            case AddOrderBid.AddOrderBidInputNames.resetAssocClientContact:
                result = (Formatters.getInt(formData, 'newOrderAssociationId') <= 0) ? true : false;
                break;

            default:
                result = false;
                break;
        }

        return result;
    }

    // Renderers
    public renderHeaderButtonToolbar = (formData: any) => {
        let buttonProps = {
            bsSize: 'sm',
        };

        let saveChangesButtonProps = {
            bsStyle: 'default',
            bsSize: 'sm',
            disabled: true,
            title: this.appText('create-bid'),
        };

        if (this.getFormChangesValidateSave(formData)) {
            saveChangesButtonProps = {
                bsStyle: 'success',
                bsSize: 'sm',
                disabled: false,
                title: this.appText('create-bid')
            };
        }

        return (
            <div>
                <Well className="header-well-buttontoolbar">
                    <Row>
                        <Col xs={9}>
                            <ButtonToolbar>
                                <Button {...buttonProps} bsStyle="info" onClick={e => { this.handleAddClientButton(e); }}>
                                    <i className="glyphicon glyphicon-plus" /> {this.appText('new-client')}
                                </Button>

                                <Button {...buttonProps} bsStyle="info" onClick={e => { this.handleAddAssociationButton(e); }}>
                                    <i className="glyphicon glyphicon-plus" /> {this.appText('new-association')}
                                </Button>

                                <Button {...buttonProps} bsStyle="danger" onClick={e => { this.handleAddNewBidCancelButton(e); }}>
                                    <i className="fa fa-trash-o" />  {this.appText('cancel-new-bid')}
                                </Button>
                            </ButtonToolbar>
                        </Col>
                        <Col xs={3}>
                            <ButtonToolbar>
                                <Button {...saveChangesButtonProps} onClick={e => { this.handleConfirmAddBidButton(e); }}><i className="glyphicon glyphicon-floppy-disk" /> {saveChangesButtonProps.title}</Button>
                                { /* <Button {...saveChanesButtonProps} onClick={e => { this.handleCreateBidButton(e); }}><i className="glyphicon glyphicon-floppy-disk" /> {saveChangesButtonProps.title}</Button> */ }
                            </ButtonToolbar>
                        </Col>
                    </Row>
                </Well>
            </div>
        );
    }

    public renderFooterButtonToolbar = (formData: any) => {
        let buttonProps = {
            bsSize: 'sm',
        };

        let saveChangesButtonProps = {
            bsStyle: 'default',
            bsSize: 'sm',
            disabled: true,
            title: this.appText('create-bid'),
        };

        if (this.getFormChangesValidateSave(formData)) {
            // if (this.getFormChangesCount() >= 2) {
            saveChangesButtonProps = {
                bsStyle: 'success',
                bsSize: 'sm',
                disabled: false,
                title: this.appText('create-bid')
            };
        }

        return (
            <div>
                <Well className="header-well-buttontoolbar">
                    <Row>
                        <Col xs={12}>
                            <ButtonToolbar>
                                <Button {...saveChangesButtonProps} onClick={e => { this.handleConfirmAddBidButton(e); }}><i className="glyphicon glyphicon-floppy-disk" /> {saveChangesButtonProps.title}</Button>
                                { /* <Button {...saveChanesButtonProps} onClick={e => { this.handleCreateBidButton(e); }}><i className="glyphicon glyphicon-floppy-disk" /> {saveChangesButtonProps.title}</Button> */ }
                            </ButtonToolbar>
                        </Col>
                    </Row>
                </Well>
            </div>
        );
    }

    public renderSectionClientInfo(formData: any, validReqs: boolean = true) {
        const defaultInputProps = { colLabelXs: 4, colInputXs: 7 };

        const inputPropsVirtualizedSelect = _.merge({}, defaultInputProps, {
            className: 'inventory-detail-input-75', // inventory-detail-input-full-width
        });

        const newOrderSelectedClient = _.get(formData, 'newOrderSelectedClient');
        const newOrderSelectedClientName = Formatters.getStr(newOrderSelectedClient, 'name');
        const newOrderClientId = _.get(formData, 'newOrderClientId');

        let newOrderClientData = _.get(formData, 'newOrderClientOptions', []).slice(this.state.skipClient, this.state.skipClient + this.virtualizedDropdownPageSize);
        const newOrderClientDataFound = _.find(newOrderClientData, [ 'value', newOrderClientId ]);
        let newOrderClientValue: any; // = undefined;
        if (newOrderClientDataFound) { newOrderClientValue = _.get(formData, 'newOrderClientId'); }

        let result = (
            <div>
                <DetailFormCommon.InventoryDetailFormSection title={this.appText('client-info')}>
                    <KendoWrappers.KendoDropdownListInputChildren
                        name="newOrderClientId"
                        clearable={false}
                        filterable={true}
                        disabled={this.getInputDisabled(AddOrderBid.AddOrderBidInputNames.clientName, formData)}
                        readonly={this.getInputDisabled(AddOrderBid.AddOrderBidInputNames.clientName, formData)}
                        onFilterChange={(e) => { this.handleClientDropdownFilterChange(e, formData); }}
                        label={this.appText('client-name')}
                        value={newOrderClientValue} // {_.get(formData, 'newOrderClientId')}
                        data={newOrderClientData} // {formData.newOrderClientOptions.slice(this.state.skipClient, this.state.skipClient + this.virtualizedDropdownPageSize)}
                        defaultItem={{ text: newOrderSelectedClientName, value: newOrderClientId }}
                        // defaultItem={{ text: Formatters.getStr(newOrderSelectedClient, 'name'), value: newOrderClientId }}
                        // defaultItem={{ text: Formatters.getStr(formData, 'client.name'), value: _.get(formData, 'newOrderClientId') }}
                        // defaultItem={{ text: Formatters.getStr(formData, 'newOrderSelectedAssociationClient.name'), value: _.get(formData, 'newOrderClientId') }}
                        virtual={{
                            total: _.size(formData.newOrderClientOptions),
                            pageSize: this.virtualizedDropdownPageSize,
                            skip: this.state.skipClient,
                        }}
                        onPageChange={(e) => { this.handleClientDropdownPageChange(e, formData); }}
                        textField="text"
                        valueField="value"
                        onChange={(o) => { this.handleFormInputClientChange({target: { name: 'newOrderClientId', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') }}, o); }}
                        {...inputPropsVirtualizedSelect}
                    >
                        <div className="edit-order-set-due-date-button-wrapper">
                            <Button
                                disabled={this.getInputDisabled(AddOrderBid.AddOrderBidInputNames.clientName, formData)}
                                bsSize="xs"
                                bsStyle="info"
                                onClick={e => { this.handleAddClientButton(e); }}
                            >
                                <i className="glyphicon glyphicon-plus" /> {this.appText('new-client')}
                            </Button>
                        </div>
                    </KendoWrappers.KendoDropdownListInputChildren>

                    <KendoWrappers.KendoDropdownListInputChildren
                        name="newOrderClientContactId"
                        clearable={true} // {false}
                        filterable={true}
                        disabled={this.getInputDisabled(AddOrderBid.AddOrderBidInputNames.clientContact, formData)}
                        readonly={this.getInputDisabled(AddOrderBid.AddOrderBidInputNames.clientContact, formData)}
                        onFilterChange={(e) => { this.handleClientContactDropdownFilterChange(e, formData); }}
                        label={this.appText('client-contact')}
                        value={_.get(formData, 'newOrderClientContactId')}
                        data={formData.newOrderClientContactOptions.slice(this.state.skipClientContact, this.state.skipClientContact + this.virtualizedDropdownPageSize)}
                        defaultItem={{ text: Formatters.composeClientContactFormal(_.get(formData, 'newOrderSelectedAssociation.contact')), value: _.get(formData, 'newOrderClientContactId') }}
                        // defaultItem={{ text: Formatters.getStr(formData, 'newOrderSelectedAssociation.contact.firstName'), value: _.get(formData, 'newOrderClientContactId') }}
                        // defaultItem={{ text: Formatters.getStr(formData, 'defaultContactName'), value: _.get(formData, 'newOrderClientContactId') }}
                        virtual={{
                            total: _.size(formData.newOrderClientContactOptions),
                            pageSize: this.virtualizedDropdownPageSize,
                            skip: this.state.skipClientContact,
                        }}
                        onPageChange={(e) => { this.handleClientContactDropdownPageChange(e, formData); }}
                        textField="text"
                        valueField="value"
                        onChange={(o) => { this.handleFormInputClientContactChange({target: { name: 'newOrderClientContactId', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') }}, o); }}
                        {...inputPropsVirtualizedSelect}
                    >
                        <div className="edit-order-set-due-date-button-wrapper">
                            <Button
                                disabled={this.getInputDisabled(AddOrderBid.AddOrderBidInputNames.clientContact, formData)}
                                bsSize="xs"
                                bsStyle="info"
                                onClick={e => { this.handleAddClientContactButton(e); }}
                            >
                                <i className="glyphicon glyphicon-plus" /> {this.appText('new-client-contact')}
                            </Button>
                        </div>
                    </KendoWrappers.KendoDropdownListInputChildren>
                </DetailFormCommon.InventoryDetailFormSection>
            </div>
        );

        return result;
    }

    public renderSectionAssociationInfo(formData: any, validReqs: boolean = true) {
        const defaultInputProps = { colLabelXs: 4, colInputXs: 7 };

        const inputPropsVirtualizedSelect = _.merge({}, defaultInputProps, {
            className: 'inventory-detail-input-75', // inventory-detail-input-full-width
        });

        const inputPropsRoleCheckbox = _.merge({}, defaultInputProps, {
            className: '_inventory-detail-input-full-width'
        });

        const newOrderSelectedAssociation = _.get(formData, 'newOrderSelectedAssociation');
        const newOrderSelectedAssociationName = Formatters.getStr(newOrderSelectedAssociation, 'name');
        const newOrderAssociationId = _.get(formData, 'newOrderAssociationId');

        const newOrderSelectedAssociationLocation = _.get(newOrderSelectedAssociation, 'location', {});
        const newOrderSelectedAssociationClient = _.get(formData, 'newOrderSelectedAssociationClient');

        const assocInfoAssocNameText = `${newOrderAssociationId} - ${newOrderSelectedAssociationName}`;

        const assocInfoAssocClientId = Formatters.getInt(newOrderSelectedAssociationClient, 'clientId');
        const assocInfoAssocClientText = `${assocInfoAssocClientId} - ${Formatters.getStr(newOrderSelectedAssociationClient, 'name')}`;

        const assocInfoAssocContact = _.get(newOrderSelectedAssociation, 'contact');
        const assocInfoAssocContactText = Formatters.composeClientContactFormal(assocInfoAssocContact);

        let newOrderAssociationData = _.get(formData, 'newOrderAssociationOptions', []).slice(this.state.skipAssociation, this.state.skipAssociation + this.virtualizedDropdownPageSize);
        const newOrderAssociationDataFound = _.find(newOrderAssociationData, [ 'value', newOrderAssociationId ]);
        let newOrderAssociationValue: any;
        if (newOrderAssociationDataFound) { newOrderAssociationValue = _.get(formData, 'newOrderAssociationId'); }

        let selectedAssocInfo = (<div />);
        if (newOrderAssociationId > 0) {
            selectedAssocInfo = (
                <div>
                    <hr />

                    <KendoWrappers.KendoDisabledInputHidden
                        name="_assoc_info_assocName"
                        label={this.appText('association-info.association-name')}
                        value={(<Link target="_blank" to={NavigationLinks.associationDetail(newOrderAssociationId)} title={assocInfoAssocNameText}>{assocInfoAssocNameText}</Link>)}
                        {...inputPropsVirtualizedSelect}
                    />

                    <KendoWrappers.KendoDisabledInputHidden
                        name="_assoc_info_assocLocationCity"
                        label={this.appText('association-info.association-city')}
                        value={Formatters.getStr(newOrderSelectedAssociationLocation, 'city')}
                        {...inputPropsVirtualizedSelect}
                    />

                    <KendoWrappers.KendoDisabledInputHidden
                        name="_assoc_info_assocLocationStateCode"
                        label={this.appText('association-info.association-state')}
                        value={Formatters.getStr(newOrderSelectedAssociationLocation, 'stateCode')}
                        {...inputPropsVirtualizedSelect}
                    />

                    <KendoWrappers.KendoDisabledInputHidden
                        name="_assoc_info_assocLocationPostalCode"
                        label={this.appText('association-info.association-zip')}
                        value={Formatters.getStr(newOrderSelectedAssociationLocation, 'postalCode')}
                        {...inputPropsVirtualizedSelect}
                    />

                    <KendoWrappers.KendoDisabledInputHidden
                        name="_assoc_info_assocHOAType"
                        label={this.appText('association-info.association-hoa-type')}
                        value={Formatters.getStr(newOrderSelectedAssociation, 'associationType.name')}
                        {...inputPropsVirtualizedSelect}
                    />

                    <KendoWrappers.KendoDisabledInputHidden
                        name="_assoc_info_assocDetailsNumUnits"
                        label={this.appText('association-info.association-num-units')}
                        value={Formatters.getInt(newOrderSelectedAssociation, 'details.numberUnits')}
                        {...inputPropsVirtualizedSelect}
                    />

                    {(assocInfoAssocClientId) ?
                        (<KendoWrappers.KendoDisabledInputHidden
                            name="_assoc_info_assocClientInfo"
                            label={this.appText('association-info.association-client')}
                            value={(<Link target="_blank" to={NavigationLinks.clientDetail(assocInfoAssocClientId)} title={assocInfoAssocClientText}>{assocInfoAssocClientText}</Link>)}
                            {...inputPropsVirtualizedSelect}
                        />) : (<div />)
                    }

                    <KendoWrappers.KendoDisabledInputHidden
                        name="_assoc_info_assocClientInfo"
                        label={this.appText('association-info.association-contact')}
                        value={assocInfoAssocContactText}
                        {...inputPropsVirtualizedSelect}
                    />

                    { /*
                    <KendoWrappers.KendoCheckbox
                        name="newOrderResetAssociationClientContact"
                        disabled={this.getInputDisabled(AddOrderBid.AddOrderBidInputNames.resetAssocClientContact, formData)}
                        label={this.appText('association-info.reset-association-client-contact')}
                        value={_.get(formData, 'newOrderResetAssociationClientContact')}
                        onChange={(n, v) => { this.handleFormInputChange(n, v); }}
                        {...inputPropsRoleCheckbox}
                    /> */ }
                </div>
            );
        }

        let result = (
            <div>
                <DetailFormCommon.InventoryDetailFormSection title={this.appText('association-info')}>
                    <KendoWrappers.KendoDropdownListInputChildren
                        name="newOrderAssociationId"
                        clearable={false}
                        filterable={true}
                        disabled={this.getInputDisabled(AddOrderBid.AddOrderBidInputNames.associationName, formData)}
                        readonly={this.getInputDisabled(AddOrderBid.AddOrderBidInputNames.associationName, formData)}
                        onFilterChange={(e) => { this.handleAssociationDropdownFilterChange(e, formData); }}
                        label={this.appText('association-name')}
                        value={newOrderAssociationValue} // {_.get(formData, 'newOrderAssociationId')}
                        data={newOrderAssociationData}
                        // data={formData.newOrderAssociationOptions.slice(this.state.skipAssociation, this.state.skipAssociation + this.virtualizedDropdownPageSize)}
                        defaultItem={{ text: newOrderSelectedAssociationName, value: newOrderAssociationId }}
                        // defaultItem={{ text: Formatters.getStr(formData, 'association.name'), value: _.get(formData, 'newOrderAssociationId') }}
                        virtual={{
                            total: _.size(formData.newOrderAssociationOptions),
                            pageSize: this.virtualizedDropdownPageSize,
                            skip: this.state.skipAssociation,
                        }}
                        onPageChange={(e) => { this.handleAssociationDropdownPageChange(e, formData); }}
                        textField="text"
                        valueField="value"
                        onChange={(o) => { this.handleFormInputAssociationChange({target: { name: 'newOrderAssociationId', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') }}, o); }}
                        {...inputPropsVirtualizedSelect}
                    >
                        <div className="edit-order-set-due-date-button-wrapper">
                            <Button
                                disabled={this.getInputDisabled(AddOrderBid.AddOrderBidInputNames.associationName, formData)}
                                bsSize="xs"
                                bsStyle="info"
                                onClick={e => { this.handleAddAssociationButton(e); }}
                            >
                                <i className="glyphicon glyphicon-plus" /> {this.appText('new-association')}
                            </Button>
                        </div>
                    </KendoWrappers.KendoDropdownListInputChildren>

                    {selectedAssocInfo}

                </DetailFormCommon.InventoryDetailFormSection>
            </div>
        );

        return result;
    }

    public renderSectionOrderInfo(formData: any, validReqs: boolean = true) {
        const defaultInputProps = { colLabelXs: 4, colInputXs: 7 };

        const inputPropsVirtualizedSelect = _.merge({}, defaultInputProps, {
            className: 'inventory-detail-input-full-width',
        });

        let result = (
            <div>
                <DetailFormCommon.InventoryDetailFormSection title={this.appText('order-info')}>
                    <KendoWrappers.KendoDropdownListInput
                        name="newOrderManagerUserId"
                        clearable={false}
                        filterable={true}
                        disabled={this.getInputDisabled(AddOrderBid.AddOrderBidInputNames.projectManager, formData)}
                        readonly={this.getInputDisabled(AddOrderBid.AddOrderBidInputNames.projectManager, formData)}
                        onFilterChange={(e) => { this.handleManagerUserDropdownFilterChange(e, formData); }}
                        label={this.appText('order-manager-user')}
                        value={_.get(formData, 'newOrderManagerUserId')}
                        data={formData.newOrderManagerUserOptions.slice(this.state.skipManagerUser, this.state.skipManagerUser + this.virtualizedDropdownPageSize)}
                        defaultItem={{ text: Formatters.getStr(formData, 'managerUser.name'), value: _.get(formData, 'newOrderManagerUserId') }}
                        virtual={{
                            total: _.size(formData.newOrderManagerUserOptions),
                            pageSize: this.virtualizedDropdownPageSize,
                            skip: this.state.skipManagerUser,
                        }}
                        onPageChange={(e) => { this.handleManagerUserDropdownPageChange(e, formData); }}
                        textField="text"
                        valueField="value"
                        onChange={(o) => { this.handleFormInputChange({target: { name: 'newOrderManagerUserId', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') }}, o); }}
                        {...inputPropsVirtualizedSelect}
                    />
                    <KendoWrappers.KendoDropdownListInput
                        name="newOrderSiteInspectorUserId"
                        clearable={false}
                        filterable={true}
                        disabled={this.getInputDisabled(AddOrderBid.AddOrderBidInputNames.siteInspector, formData)}
                        readonly={this.getInputDisabled(AddOrderBid.AddOrderBidInputNames.siteInspector, formData)}
                        onFilterChange={(e) => { this.handleSiteInspectorUserDropdownFilterChange(e, formData); }}
                        label={this.appText('order-site-inspector-user')}
                        value={_.get(formData, 'newOrderSiteInspectorUserId')}
                        data={formData.newOrderSiteInspectorUserOptions.slice(this.state.skipSiteInspectorUser, this.state.skipSiteInspectorUser + this.virtualizedDropdownPageSize)}
                        defaultItem={{ text: Formatters.getStr(formData, 'siteInspectorUser.name'), value: _.get(formData, 'newOrderSiteInspectorUserId') }}
                        virtual={{
                            total: _.size(formData.newOrderSiteInspectorUserOptions),
                            pageSize: this.virtualizedDropdownPageSize,
                            skip: this.state.skipSiteInspectorUser,
                        }}
                        onPageChange={(e) => { this.handleSiteInspectorUserDropdownPageChange(e, formData); }}
                        textField="text"
                        valueField="value"
                        onChange={(o) => { this.handleFormInputChange({target: { name: 'newOrderSiteInspectorUserId', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') }}, o); }}
                        {...inputPropsVirtualizedSelect}
                    />
                </DetailFormCommon.InventoryDetailFormSection>
            </div>
        );

        return result;
    }

    public renderModalConfirmAddBid = () => {
        return (
            <Modal enforceFocus={false} show={this.state.showModalConfirmAddBid} onHide={() => this.handleCancelAndCloseModalConfirmAddBid()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.confirm-add-bid.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {this.appText('modals.confirm-add-bid.body')}
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" onClick={(e) => { this.handleConfirmAddBidButtonAction(e); }}>{this.appText('modals.confirm-add-bid.confirm')}</Button>
                    <Button onClick={() => this.handleCancelAndCloseModalConfirmAddBid()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public render() {
        const { lookups, ...rest } = this.props;

        const pageTitle = this.appTextPageTitle();
        let page = (<div />);

        if (!_.isEmpty(lookups)) {
            const formData = this.getFormData(lookups);

            if (this.getFormChangesResetRequired() || !_.size(this.originalFormDataCache)) {
                this.originalFormDataCache = _.cloneDeep(formData);
            }

            page = (
                <div>
                    {this.renderModalConfirmAddBid()}
                    <Row>
                        <Col xs={12} xl={9}>
                            {/* Header toolbar */}
                            <Row>
                                <Col xs={12}>
                                    <div>
                                        {this.renderHeaderButtonToolbar(formData)}
                                    </div>
                                </Col>
                            </Row>

                            <Row>
                                <Col xs={12}>
                                    <WhiteBoxPageSection {...this.kendoFormWhiteBoxSectionProps} title={this.appText('add-new-bid')}>
                                        {this.renderSectionClientInfo(formData, true)}
                                        {this.renderSectionAssociationInfo(formData, true)}
                                        {this.renderSectionOrderInfo(formData, true)}
                                    </WhiteBoxPageSection>
                                </Col>
                            </Row>

                            <Row>
                                <Col xs={12}>
                                    <div>
                                        {this.renderFooterButtonToolbar(formData)}
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </div>
            );
        }

        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrlLookups = () => ApiEndpoints.getLookupsByKeyUrl([
    LK.LookupKeys.Regions,
    LK.LookupKeys.Clients,
    LK.LookupKeys.AssociationsAddBid,
    LK.LookupKeys.Users,
]);

const mapPropsToConfigs = (props) => {
    return ReduxQueries.Queries.getLookupsByKeyRequest(
        getApiUrlLookups(),
        ReduxQueries.PropsAccessToken(props)
    );
};

const mapStateToProps = (state, props) => {
    const mapped = ReduxQueries.MapQueryStateToPropsByUrl(getApiUrlLookups(), state, props, {
        lastRequestDate: new Date().getTime(),
        addBid: {
            redirect: state.addBid.redirect,
            clientId: state.addBid.clientId,
            clientContactId: state.addBid.clientContactId,
            associationId: state.addBid.associationId,
            projectManagerUserObjId: state.addBid.projectManagerUserObjId,
            siteInspectorUserObjId: state.addBid.siteInspectorUserObjId,
        }
    });

    return mapped;
};

const mapDispatchToProps = {
    newOrder: MutateActions.newOrderMutateAsync,
    navigateTo: (path) => push(path),
};

const AddOrderBidContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
  )(AddOrderBid);

export default withRouter(AddOrderBidContainer);



// WEBPACK FOOTER //
// ./src/order/add-bid.tsx